import { getThreadParticipantName } from './emailThread';

/** SMS threads from an unknown number carry a placeholder fish id with this prefix. */
export const PLACEHOLDER_FISH_ID_PREFIX = 'temp-';

// Only an SMS thread's id is a fish id, so the SMS reader requires the SMS
// shape: the selected thread can still be another channel's record right
// after the channel switches.
const THREAD_FISH_ID = {
  SMS: (thread) =>
    Array.isArray(thread.SMSThread) &&
    !thread.id?.startsWith(PLACEHOLDER_FISH_ID_PREFIX)
      ? thread.id
      : null,
  APP: (thread) => thread.otherParticipant?.fish?.id,
  EMAIL: (thread) => thread.fishId,
};

/**
 * The fish on the other side of a thread, or null when it is a staff member
 * or an unknown SMS number.
 */
export const getThreadFishId = (thread, channel) =>
  THREAD_FISH_ID[channel]?.(thread) ?? null;

/**
 * Normalizes the three thread shapes the Inbox handles — APP conversations,
 * EmailThread records and SMS threads — into the fields a list row or a
 * conversation header needs.
 */
export const getThreadDisplayInfo = (thread, selectedChannel) => {
  if (selectedChannel === 'APP' || thread.method === 'APP') {
    // APP thread structure
    const lastMessage = thread.messages?.[thread.messages.length - 1];
    return {
      name:
        thread.otherParticipant?.name ||
        thread.otherParticipant?.fish?.fishProfiles?.[0]?.fName ||
        'Unknown',
      timestamp: thread.lastMessageAt || lastMessage?.receivedAt,
      lastMessageBody: lastMessage?.messageBody || 'No messages',
      hasMessages: thread.messages?.length > 0,
    };
  }

  if (selectedChannel === 'EMAIL' || thread.method === 'EMAIL') {
    // Email rows are EmailThread records. The list query carries no message
    // bodies, so the subject stands in for a preview snippet.
    return {
      name: getThreadParticipantName(thread) || 'Unknown',
      timestamp: thread.updatedAt,
      lastMessageBody: thread.subject || '(no subject)',
      hasMessages: true,
    };
  }

  // SMS thread structure
  const lastMessage = thread.SMSThread?.[thread.SMSThread.length - 1];
  const profiles = thread.fishProfiles ?? [];
  // Preferred profile: the MEMBER profile if present, else the most recently
  // created one (mirrors the API's getPreferredProfile).
  const preferredProfile =
    profiles.find((fp) => fp.profileType === 'MEMBER') ??
    [...profiles].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
  const fishName = preferredProfile
    ? `${preferredProfile.fName ?? ''} ${preferredProfile.lName ?? ''}`.trim()
    : '';
  return {
    name: fishName || thread.phoneNumber || 'Unknown',
    timestamp: lastMessage?.sentAt || lastMessage?.receivedAt,
    lastMessageBody: lastMessage?.messageBody || 'No messages',
    hasMessages: thread.SMSThread?.length > 0,
  };
};
